import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

export default class extends Controller {
  static targets = ["prompt", "submit", "fileError"];
  static values = { uploadUrl: String };
  static outlets = ["admin--massimo--attachments"];

  // The massimo_source cookie belongs to a rec prompt prefilled into the box (the digest deep
  // link sets them together). A Home render without that prefill means it was abandoned — drop it
  // so an unrelated typed prompt can't inherit the rec.
  connect() {
    if (!this.hasPromptTarget || !this.promptTarget.value.trim()) {
      this.#clearSourceCookie();
    }
  }

  submit(event) {
    event.preventDefault();
    const prompt = this.promptTarget.value.trim();
    if (!prompt && this.adminMassimoAttachmentsOutlet.count === 0) return;
    this.#send(prompt, undefined, true);
  }

  clearFileError() {
    this.fileErrorTarget.hidden = true;
  }

  insert(event) {
    const prompt = event.params.prompt;
    if (!prompt) return;
    this.promptTarget.value = prompt;
    this.promptTarget.focus();
    this.promptTarget.setSelectionRange(prompt.length, prompt.length);
  }

  submitWithPrompt(event) {
    const prompt = event.params.prompt;
    if (!prompt) return;
    this.#send(prompt, event.params.suggestionKey);
  }

  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.submit(event);
    }
  }

  // The hand-off reloads the page, which a picked File can't survive, so upload first and pass the
  // signed blob ids along for the chat form to send.
  async #send(prompt, suggestionKey, withSource = false) {
    if (this.submitTarget.disabled) return;
    const attachments = this.adminMassimoAttachmentsOutlet;
    this.submitTarget.disabled = true;
    attachments.disabled = true;
    let uploaded;
    try {
      uploaded = await Promise.all(
        attachments.files.map((file) => this.#upload(file)),
      );
    } catch (error) {
      console.error("Massimo Home upload failed:", error);
      this.fileErrorTarget.hidden = false;
      this.submitTarget.disabled = false;
      attachments.disabled = false;
      return;
    }
    // Only once the uploads land, so a failed upload's retry keeps the digest recommendation.
    const source = withSource ? this.#consumeSourceCookie() : undefined;
    this.#startChat(prompt, suggestionKey, source, uploaded);
  }

  // Hand the prompt to Mo and reload so the chat panel opens with it. The
  // autosubmit flag tells the chat form to send immediately — scoped to this
  // home-page entry point so deep links / "Ask Mo" banners only pre-fill.
  // The prompt rides sessionStorage (not a cookie) so long pastes aren't capped
  // by the ~4KB per-cookie limit; suggestionKey, when set, lets the autosubmitted
  // chat record the home-card click so the suggestion rotates out.
  #startChat(prompt, suggestionKey, source, uploaded = []) {
    sessionStorage.setItem("massimo-prompt", prompt);
    if (uploaded.length > 0) {
      sessionStorage.setItem("massimo-files", JSON.stringify(uploaded));
    } else {
      sessionStorage.removeItem("massimo-files");
    }
    sessionStorage.setItem("massimo-fullscreen", "true");
    sessionStorage.setItem("massimo-open", "true");
    sessionStorage.setItem("massimo-chat-id", "new");
    sessionStorage.setItem("massimo-autosubmit", "true");
    if (source) {
      sessionStorage.setItem("massimo-source", source);
    } else {
      // Ordinary prompt — discard any pending rec source so it can't attach to a later chat.
      sessionStorage.removeItem("massimo-source");
      this.#clearSourceCookie();
    }
    if (suggestionKey) {
      sessionStorage.setItem("massimo-suggestion-key", suggestionKey);
    } else {
      sessionStorage.removeItem("massimo-suggestion-key");
    }
    window.location.reload();
  }

  // A digest deep link parks the recommendation eid in a cookie while its prompt sits in the Home
  // box; consume it only when the operator actually submits, so the chat links to the recommendation.
  #consumeSourceCookie() {
    const match = document.cookie.match(/massimo_source=([^;]*)/);
    if (!match) return undefined;
    this.#clearSourceCookie();
    return decodeURIComponent(match[1].replace(/\+/g, " "));
  }

  #upload(file) {
    return new Promise((resolve, reject) => {
      new DirectUpload(file, this.uploadUrlValue).create((error, blob) => {
        if (error) {
          reject(error);
        } else {
          resolve({ signedId: blob.signed_id, filename: blob.filename });
        }
      });
    });
  }

  #clearSourceCookie() {
    document.cookie = "massimo_source=; path=/; max-age=0";
  }
}
